import { matchQuery } from '@tanstack/react-query';
import { ACTIVE_GROUND_TRIP_STATUSES } from '@lp/features/RoutePlanner/config';
import {
  CheckpointModeType,
  CheckpointTypeEnum,
  DropOffCheckpointType,
  GroundTripCheckpointType,
  GroundTripLocationType,
  GroundTripStatus,
  LogisticsGroundTrip,
  PickUpCheckpointType,
} from '@/api/apis/drive-service';
import { queryClient } from '@/queries';
import { driveServiceTripsQueryKeys } from '@/queries/drive-service';
import { LogisticsLegResourceType, TmdxDriveAffiliateGroundLeg, TmdxDriveTedsGroundLeg } from '@/api/apis/route';
import { CaseResourceType } from '@/api/apis/case';

export const getTripLocationTitle = (data: GroundTripCheckpointType): string => {
  if (data.$type === CheckpointTypeEnum.UnplannedCheckpointResponse) {
    return data.gpsLocation?.address || '';
  }

  const location = data.locationData;
  const detailedAddress = [location.addressLine, location.city, location.country].filter(Boolean).join(', ');

  switch (location.locationType) {
    case GroundTripLocationType.DonorHospital:
      return `DH ${location.name} ${detailedAddress}`;
    case GroundTripLocationType.TxCenter:
      return `TXC ${location.name} ${detailedAddress}`;
    case GroundTripLocationType.Hub:
      return `Hub ${location.code} ${detailedAddress}`;
    case GroundTripLocationType.Airport:
      return `Airport ${location.iata || location.icao}, ${detailedAddress}`;
    case GroundTripLocationType.Fbo:
      return `FBO ${location.airportIata || location.airportIcao}, ${detailedAddress}`;
    case GroundTripLocationType.CustomAddress:
    default:
      return detailedAddress;
  }
};

export const checkResourceOnActiveGroundTrip = (groundTripIds: string[], resourceId?: string) => {
  if (!resourceId || !groundTripIds.length) return false;

  const groundTripsCache = queryClient.getQueriesData<LogisticsGroundTrip>({
    predicate: (query) => {
      return groundTripIds.some((groundTripId) =>
        matchQuery({ queryKey: driveServiceTripsQueryKeys.getItem(groundTripId) }, query),
      );
    },
  });

  const groundTrips = groundTripsCache.map(([, trip]) => trip).filter((i): i is LogisticsGroundTrip => Boolean(i));

  if (!groundTrips.length) return false;

  return groundTrips.some((trip) => {
    if (!ACTIVE_GROUND_TRIP_STATUSES.includes(trip.status)) return false;

    return trip.passengers.some((p) => p.passengerId === resourceId);
  });
};

export const validateCheckpointTime = (
  leg: Pick<TmdxDriveLegValidateValues, 'departureTime' | 'arrivalTime'>,
  dateTimeString: string,
) => {
  const checkpointTime = new Date(dateTimeString).getTime();
  const legStartTime = new Date(leg.departureTime).getTime();
  const legEndTime = new Date(leg.arrivalTime).getTime();

  const isAfterStart = checkpointTime >= legStartTime;
  const isBeforeEnd = legEndTime >= checkpointTime;
  return isAfterStart && isBeforeEnd;
};

type TmdxDriveLegValidateValues = Pick<
  TmdxDriveAffiliateGroundLeg | TmdxDriveTedsGroundLeg,
  'resources' | 'departureTime' | 'arrivalTime'
>;

const checkGroundTripDateTime = (
  leg: TmdxDriveLegValidateValues,
  checkpoints: GroundTripCheckpointType[],
  isMergedTrip: boolean = false,
) => {
  if (isMergedTrip) return true;

  const pickUpCheckpoints = checkpoints.filter((c) => c.modeData.mode === CheckpointModeType.PickUp);
  const dropOffCheckpoints = checkpoints.filter((c) => c.modeData.mode === CheckpointModeType.DropOff);

  const isPickUpAfterLegStart = pickUpCheckpoints.every((c) => {
    const data = c.modeData as PickUpCheckpointType;

    return validateCheckpointTime(leg, data.pickUpDateTime);
  });

  const isDropOffBeforeLegEnd = dropOffCheckpoints.every((c) => {
    const data = c.modeData as DropOffCheckpointType;

    return validateCheckpointTime(leg, data.dropOffDateTime);
  });

  return isPickUpAfterLegStart && isDropOffBeforeLegEnd;
};

const checkGroundTripPassengers = (resources: LogisticsLegResourceType[], checkpoints: GroundTripCheckpointType[]) => {
  const resourcesIds = resources
    .filter((r) =>
      [CaseResourceType.ClinicalSpecialist, CaseResourceType.OCSConsole, CaseResourceType.Surgeon].includes(
        r.resourceType,
      ),
    )
    .map((r) => r.resourceId);

  const passengersIds = new Set(
    checkpoints.flatMap((p) => {
      switch (p.modeData.mode) {
        case CheckpointModeType.PickUp:
          return p.modeData.passengerIdsToPickUp;
        case CheckpointModeType.DropOff:
          return p.modeData.passengerIdsToDrop;
        default:
          return [];
      }
    }),
  );

  return resourcesIds.every((id) => passengersIds.has(id));
};

export const getGroundTripWarnings = (
  leg: TmdxDriveLegValidateValues,
  trip: LogisticsGroundTrip,
  isEditLeg: boolean = false,
): string[] => {
  let warnings: string[] = [];

  const isCorrectTripDateTime = checkGroundTripDateTime(leg, trip.tripPlan.checkpoints, trip.isMergedTrip);

  if (!isCorrectTripDateTime) {
    warnings = [...warnings, `Some trip plan checkpoints go beyond leg's departure or arrival time.`];
  }

  const isNewTrip = trip.status === GroundTripStatus.New;
  const areAllResourcesIncluded = checkGroundTripPassengers(leg.resources, trip.tripPlan.checkpoints);

  if (!isNewTrip && !areAllResourcesIncluded) {
    warnings = [
      ...warnings,
      'The resources involved in this ground leg are not included as passengers in the trip plan.',
    ];
  }

  const isActiveTrip = ACTIVE_GROUND_TRIP_STATUSES.includes(trip.status);

  if (isEditLeg && isActiveTrip) {
    warnings = [
      ...warnings,
      'The trip linked to this ground leg is already In Progress, Completed, or Canceled status.',
    ];
  }

  return warnings;
};
